import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { SITE_URL, SITE_BRAND } from "@/lib/seo-config";
import { METIER_LIST } from "@/lib/metier-types";
import {
  Wrench, Zap, Home, Hammer, Key, Layers, Building, TreePine, Square,
  UtensilsCrossed, Trees, Flame, Bath, Warehouse, Bell, DoorClosed, Wind,
  Snowflake, Shovel, Paintbrush, ArrowRight, Grid3x3, ShieldCheck,
} from "lucide-react";

const ICONS = {
  Wrench, Zap, Home, Hammer, Key, Layers, Building, TreePine, Square,
  UtensilsCrossed, Trees, Flame, Bath, Warehouse, Bell, DoorClosed, Wind,
  Snowflake, Shovel, Paintbrush, Grid3x3,
  Bricks: Layers,
};

const seo = {
  title: `Trouver un artisan BTP par métier · ${SITE_BRAND}`,
  description: "Annuaire des 25 métiers du BTP : plombier, électricien, maçon, peintre, carreleur, chauffagiste, couvreur, menuisier… Trouvez un artisan SIRET vérifié dans votre ville et payez en toute sécurité.",
  keywords: "annuaire artisan BTP, artisan bâtiment par métier, plombier électricien maçon, artisan SIRET vérifié France, trouver artisan de confiance, devis artisan gratuit",
  canonical: "/artisan",
  ogType: "website",
  schema: [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": `Métiers BTP — ${SITE_BRAND}`,
      "description": "Annuaire des 25 métiers du bâtiment.",
      "url": `${SITE_URL}/artisan`,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Accueil", "item": SITE_URL },
        { "@type": "ListItem", "position": 2, "name": "Artisans par métier", "item": `${SITE_URL}/artisan` },
      ],
    },
  ],
};

export default function MetierIndex() {
  return (
    <div className="min-h-screen bg-[#F7F7F9]" data-testid="metier-index">
      <SEO cfg={seo} />
      <Navbar />

      {/* HERO */}
      <section className="bg-[#0A0A0B] text-white border-b-2 border-[#0A0A0B] relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-10 pointer-events-none" />
        <div className="max-w-6xl mx-auto px-6 py-16 lg:py-20 relative">
          <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">// 25 métiers du bâtiment</div>
          <h1 className="display text-4xl lg:text-6xl tracking-tighter mt-3 max-w-4xl leading-[0.95]">
            Trouvez un <span className="text-[#FF4D00]">artisan BTP</span><br />par métier & ville.
          </h1>
          <p className="mt-6 text-zinc-300 text-base lg:text-lg max-w-3xl">
            Plombier, électricien, maçon, peintre, cuisiniste, chauffagiste RGE… Chaque artisan Batisur est SIRET vérifié auprès de l'INSEE, ses avis sont réels et votre paiement est sécurisé chez Stripe.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/register?role=demandeur">
              <button className="btn-batisur flex items-center gap-2" data-testid="metier-index-cta">
                Publier ma demande <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
            <Link to="/artisans/villes">
              <button className="btn-anthracite border-2 border-white/40">Voir par ville</button>
            </Link>
          </div>
        </div>
      </section>

      {/* GRID METIERS */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">// Explorer</div>
        <h2 className="display text-3xl lg:text-5xl tracking-tighter mt-3 max-w-3xl leading-[0.95]">
          25 métiers, 300+ villes en France, <span className="text-[#FF4D00]">1 seule sécurité.</span>
        </h2>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="metier-grid">
          {METIER_LIST.map((m) => {
            const Icon = ICONS[m.icon] || Wrench;
            return (
              <Link
                key={m.slug}
                to={`/artisan/${m.slug}`}
                className="border-2 border-[#0A0A0B] bg-white p-5 hover:bg-[#FF4D00] hover:text-white transition group shadow-[3px_3px_0_#0A0A0B]"
                data-testid={`metier-card-${m.slug}`}
              >
                <div className="w-10 h-10 border-2 border-[#0A0A0B] bg-[#FF4D00] flex items-center justify-center mb-3 group-hover:bg-white group-hover:border-white">
                  <Icon className="w-5 h-5 text-white group-hover:text-[#FF4D00]" strokeWidth={2.5} />
                </div>
                <div className="display text-xl leading-tight">{m.label}</div>
                <div className="mono text-[10px] uppercase tracking-widest mt-2 opacity-70">
                  {m.hourlyRate[0]}-{m.hourlyRate[1]}€/h
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* CONFIANCE */}
      <section className="bg-white border-t-2 border-[#0A0A0B]">
        <div className="max-w-5xl mx-auto px-6 py-16 text-center">
          <ShieldCheck className="w-10 h-10 text-[#FF4D00] mx-auto" />
          <h2 className="display text-3xl lg:text-4xl tracking-tighter mt-4">
            Tous les artisans Batisur sont vérifiés
          </h2>
          <p className="mt-4 text-zinc-700 max-w-2xl mx-auto">
            SIRET INSEE Sirene contrôlé en temps réel, assurance décennale requise, avis vérifiés uniquement par des clients payants. Zéro faux artisan, zéro acompte perdu.
          </p>
          <div className="mt-8">
            <Link to="/register?role=demandeur" className="btn-batisur">
              Créer mon compte demandeur →
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
